import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { CompetitionHeader } from './CompetitionHeader';
import { CompetitionRow, Row } from './CompetitionRow';
import type { Competitions_So5LeaderboardGroup } from './__generated__/index.graphql';

const Wrapper = styled.div`
  border-radius: var(--double-unit);
  border: 1px solid transparent;

  background:
    linear-gradient(var(--c-black), var(--c-black)) padding-box,
    linear-gradient(
        to bottom right,
        rgba(var(--rgb-color), 0.4),
        rgba(var(--rgb-color), 0.1) 40%,
        rgba(var(--rgb-color), 0.1) 55%,
        rgba(var(--rgb-color), 0.1)
      )
      border-box;
`;

type Props = {
  eventType: string;
  leaderboardGroup: Competitions_So5LeaderboardGroup;
};

export const Competitions = ({ eventType, leaderboardGroup }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const rarityType = leaderboardGroup.so5Leaderboards[0]?.rarityType;

  if (!rarityType) {
    return null;
  }

  return (
    <Wrapper
      style={
        {
          '--rgb-color': `var(--c-rgb-${toCamelCase(rarityType ?? '')})`,
        } as CSSProperties
      }
    >
      <CompetitionHeader rarity={rarityType as Rarity} />
      {!!isTabletAndAbove && (
        <Row>
          <LabelS
            bold
            uppercase
            color="var(--c-nd-600)"
            style={{ gridArea: 'competition' }}
          >
            <FormattedMessage {...glossary.competition} />
          </LabelS>
          <LabelS
            bold
            uppercase
            color="var(--c-nd-600)"
            style={{ gridArea: 'prizePool' }}
          >
            <FormattedMessage {...navLabels.prizePool} />
          </LabelS>
          <LabelS
            bold
            uppercase
            color="var(--c-nd-600)"
            style={{ gridArea: 'players' }}
          >
            <FormattedMessage {...glossary.players} />
          </LabelS>
          <LabelS
            bold
            uppercase
            color="var(--c-nd-600)"
            style={{ gridArea: 'entries' }}
          >
            <FormattedMessage {...glossary.entries} />
          </LabelS>
          <LabelS
            bold
            uppercase
            color="var(--c-nd-600)"
            style={{ gridArea: 'players' }}
          >
            <FormattedMessage {...glossary.players} />
          </LabelS>
          <div style={{ gridArea: 'join' }} />
        </Row>
      )}
      {leaderboardGroup.so5Leaderboards.map(leaderboard => {
        return (
          <CompetitionRow
            key={leaderboard.slug}
            eventType={eventType}
            leaderboard={leaderboard}
            currentTrack={leaderboardGroup.so5LeagueTracks?.find(
              track => track.entrySo5Leaderboard?.slug === leaderboard.slug
            )}
          />
        );
      })}
    </Wrapper>
  );
};

Competitions.fragments = {
  so5LeaderboardGroup: gql`
    fragment Competitions_So5LeaderboardGroup on So5LeaderboardGroup {
      slug
      so5LeagueTracks {
        slug
        ...CompetitionRow_So5LeagueTrack
      }
      so5Leaderboards {
        slug
        rarityType
        ...CompetitionRow_So5Leaderboard
      }
    }
    ${CompetitionRow.fragments.So5Leaderboard}
    ${CompetitionRow.fragments.So5LeagueTrack}
  ` as TypedDocumentNode<Competitions_So5LeaderboardGroup>,
};
